import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { versionsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import type { CreateVersionRequest, UpdateVersionRequest, PaginationParams } from '@geely/shared';

export function useVersions(modelId?: string) {
  return useQuery({
    queryKey: queryKeys.versions(modelId),
    queryFn: async () => {
      const response = await versionsApi.list(modelId);
      return response.data;
    },
  });
}

export function useVersionsPaginated(params: { modelId?: string } & Required<PaginationParams>) {
  return useQuery({
    queryKey: queryKeys.versionsPaginated(params),
    queryFn: async () => {
      const response = await versionsApi.listPaginated(params);
      return response.data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useVersion(id: string) {
  return useQuery({
    queryKey: queryKeys.version(id),
    queryFn: async () => {
      const response = await versionsApi.getById(id);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateVersionRequest) => {
      const response = await versionsApi.create(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versions'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.models });
    },
  });
}

export function useUpdateVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateVersionRequest }) => {
      const response = await versionsApi.update(id, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versions'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.models });
    },
  });
}

export function useDeleteVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await versionsApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versions'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.models });
    },
  });
}
